import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { KundeListe, TicketAngelegt, TicketKalender, TicketSpalte, Unternehmen } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen, vorgabeWiedervorlage } from "@kundeninfo/ui";
import { Auswahl, Auswahlmenue, Chip, Formular } from "@kundeninfo/ui/me-design";
import { personOption } from "./Avatar.js";
import { KiKnopf, Suchfeld } from "../../components/meHuellen.js";
import { hausfarbeChip } from "../../markenfarben.js";
import { alleFeiertage } from "./ticketFormat.js";
import { SchreibHinweis, useSchreibweg } from "./useSchreibweg.js";
import type { TicketsClient } from "./ticketsClient.js";
import "./TicketAnlegen.css";

/**
 * AUFGABE ANLEGEN (Spec „Aufgabe anlegen", F34): strikter Dialog `breite="mittel"` mit Kreuz,
 * Anlegen/Abbrechen im Fuß, gestapelte Rückfrage beim Abbrechen mit Eingaben (Hausregel
 * Modal-Bearbeitung). Vorgaben: Zuständig = ich; Spalte = erste offene Spalte rechts des
 * Eingangs, sonst Eingang; Wiedervorlage = `vorgabeWiedervorlage` (heute + 7 Tage, auf den
 * nächsten Werktag des Unternehmens gerückt — EINE Stelle in `@kundeninfo/ui`, abschaltbar,
 * Spec-Frage 136); Unternehmen = Auswahl des Boards, bei „Alle" das Arbeitsunternehmen.
 *
 * Aus der Akte steht der Kunde fest; wählbar sind nur die aktiven Unternehmen, denen er
 * zugeordnet ist (F42, Review-Fokus 4) — bei genau einem ohne Rückfrage, bei mehreren als
 * Auswahl mit dem Arbeitsunternehmen vorn, falls zugeordnet. Die Beschreibung wird
 * Beschreibung des Tickets (F9).
 *
 * Geschrieben wird über `useSchreibweg` (422 am Feld, Rest als Meldung). Der Weg reicht die
 * Antwort des Anlegens (`TicketAngelegt`) weiter; ein Nachlesen des Details gibt es nicht mehr.
 */

/** Die Felder, an denen ein 422-Befund steht; alle übrigen (etwa `customerIds`) zeigt die Meldung. */
const FELDER = ["title", "descriptionMarkdown", "columnId", "assigneeUserId", "resubmitDate"] as const;

export function TicketAnlegen(p: {
  client: TicketsClient; companies: readonly Unternehmen[]; ich: { id: string; name: string };
  vorgabeCompanyId: string | null; arbeitsunternehmenId: string | null;
  kunde?: { id: string; name: string; kuerzel: readonly string[] };
  onAbbrechen: () => void; onAngelegt: (id: string) => void;
  /** Meldet, ob Eingaben im Dialog stehen — für den Verlassen-Wächter des Bereichs (Review 17). */
  onUngespeichert?: (ungespeichert: boolean) => void;
}): ReactElement {
  const aktiv = p.companies.filter((c) => c.active);
  const erlaubt = p.kunde === undefined ? aktiv : aktiv.filter((c) => p.kunde!.kuerzel.includes(c.shortCode));
  const [start] = useState(() => [p.vorgabeCompanyId, p.arbeitsunternehmenId]
    .find((id) => id !== null && erlaubt.some((c) => c.id === id)) ?? erlaubt[0]?.id ?? "");
  const startKunden = p.kunde === undefined ? [] : [{ id: p.kunde.id, name: p.kunde.name }];

  const [companyId, setCompanyId] = useState(start);
  const [titel, setTitel] = useState("");
  const [beschreibung, setBeschreibung] = useState("");
  const [zustaendig, setZustaendig] = useState(p.ich.id);
  const [leute, setLeute] = useState<{ id: string; name: string; email?: string | null }[]>([]);
  const [spalten, setSpalten] = useState<TicketSpalte[]>([]);
  const [spalte, setSpalte] = useState("");
  const [spalteBeruehrt, setSpalteBeruehrt] = useState(false);
  const [datum, setDatum] = useState("");
  const [datumBeruehrt, setDatumBeruehrt] = useState(false);
  const [kunden, setKunden] = useState<{ id: string; name: string }[]>(startKunden);
  const [suche, setSuche] = useState("");
  const [treffer, setTreffer] = useState<KundeListe[]>([]);
  const neueId = useRef<string | null>(null);
  // `companyId` steht nur dann an einem Feld, wenn die Auswahl zu sehen ist.
  const weg = useSchreibweg<TicketAngelegt>((d) => { neueId.current = d.id; },
    { felder: erlaubt.length > 1 ? [...FELDER, "companyId"] : FELDER });

  useEffect(() => {
    let lebt = true;
    p.client.listTicketAssignees().then((r) => { if (lebt) setLeute(r.items); }).catch(() => undefined);
    p.client.listTicketColumns().then((r) => {
      if (!lebt) return;
      setSpalten(r.items);
      const sortiert = [...r.items].sort((a, b) => a.sortIndex - b.sortIndex);
      const eingang = sortiert.find((s) => s.isInbox);
      const rechts = sortiert.find((s) => s.stateKind === "OPEN" && !s.isInbox
        && s.sortIndex > (eingang?.sortIndex ?? Number.NEGATIVE_INFINITY));
      setSpalte((rechts ?? eingang ?? sortiert[0])?.id ?? "");
    }).catch(() => undefined);
    return () => { lebt = false; };
  }, [p.client]);

  // Die Vorgabe der Wiedervorlage rechnet mit „heute“ und den Feiertagen des gewählten
  // Unternehmens aus der Board-Antwort (web darf die Domäne nicht importieren). Der Server
  // speichert das Datum wie gesendet. Hat jemand das Datum angefasst, bleibt es. Solange die
  // Vorgabe lädt, ist „Anlegen“ gesperrt — sonst legte ein schneller Klick ohne Wiedervorlage an.
  const [vorgabeLaedt, setVorgabeLaedt] = useState(false);
  useEffect(() => {
    if (companyId === "" || datumBeruehrt) { setVorgabeLaedt(false); return undefined; }
    let lebt = true;
    setVorgabeLaedt(true);
    p.client.getTicketBoard({ company: companyId }).then((b) => {
      if (!lebt) return;
      const kalender: TicketKalender[] = b.kalender.filter((k) => k.companyId === companyId);
      setDatum(vorgabeWiedervorlage(b.heute, alleFeiertage(kalender)) ?? "");
    }).catch(() => undefined).finally(() => { if (lebt) setVorgabeLaedt(false); });
    return () => { lebt = false; };
  }, [p.client, companyId, datumBeruehrt]);

  const firma = p.companies.find((c) => c.id === companyId);
  const kuerzel = firma?.shortCode ?? "";
  const geaendert = titel.trim() !== "" || beschreibung.trim() !== "" || zustaendig !== p.ich.id
    || companyId !== start || spalteBeruehrt || datumBeruehrt
    || kunden.map((k) => k.id).join() !== startKunden.map((k) => k.id).join();
  const onUngespeichert = p.onUngespeichert;
  useEffect(() => { onUngespeichert?.(geaendert); }, [onUngespeichert, geaendert]);

  async function anlegen(): Promise<void> {
    neueId.current = null;
    await weg.schreiben(() => p.client.createTicket({
        companyId, title: titel, descriptionMarkdown: beschreibung.trim() === "" ? null : beschreibung,
        columnId: spalte, assigneeUserId: zustaendig === "" ? null : zustaendig,
        resubmitDate: datum === "" ? null : datum, customerIds: kunden.map((k) => k.id),
    }));
    const id = neueId.current;
    if (id !== null) p.onAngelegt(id);
  }

  async function abbrechen(): Promise<void> {
    if (weg.laeuft) return;
    if (!(await darfAbbrechen(geaendert))) return;
    p.onAbbrechen();
  }

  async function suchen(text: string): Promise<void> {
    setSuche(text);
    if (text.trim().length < 2) { setTreffer([]); return; }
    try {
      const r = await p.client.getCustomers({ search: text, limit: 20 });
      setTreffer(r.items.filter((k) => k.relations.some((rel) => rel.shortCode === kuerzel)));
    } catch {
      setTreffer([]);
    }
  }

  return (
    <Modal titel="Aufgabe anlegen" onAbbrechen={() => { void abbrechen(); }} breite="mittel"
      abbrechenGesperrt={weg.laeuft}>
      <Formular beschriftung="oben">
        <Feld id="ta-titel" label="Titel" wert={titel} bearbeitbar pflicht max={200} gesperrt={weg.laeuft}
          onChange={(w) => { setTitel(w); weg.feldRaeumen("title"); }} fehler={weg.feldFehler.title} />
        {erlaubt.length > 1 ? (
          <Auswahl beschriftung="Unternehmen" wert={companyId} gesperrt={weg.laeuft}
            {...(weg.feldFehler.companyId !== undefined ? { fehler: weg.feldFehler.companyId } : {})}
            onGeaendert={(e) => { setCompanyId(e.wert); if (p.kunde === undefined) setKunden([]); }}>
            {erlaubt.map((c) => <option key={c.id} value={c.id}>{`${c.shortCode} ${c.name}`}</option>)}
          </Auswahl>
        ) : (
          <div className="ta__unternehmen">
            <span className="ta__leise">Unternehmen</span>
            {firma === undefined
              ? <span>Der Kunde ist keinem aktiven Unternehmen zugeordnet.</span>
              : <span><Chip {...hausfarbeChip(p.companies, firma.shortCode)}>{firma.shortCode}</Chip> {firma.name}</span>}
          </div>
        )}
        {/* me-auswahlmenue (Entscheid 53): Avatar je Option; die eigene Person als „ich“ oben. */}
        <Auswahlmenue beschriftung="Zuständig" wert={zustaendig} gesperrt={weg.laeuft}
          {...(weg.feldFehler.assigneeUserId !== undefined ? { fehler: weg.feldFehler.assigneeUserId } : {})}
          onGeaendert={(e) => setZustaendig(e.wert)}>
          <option value="" data-art="niemand">Niemand</option>
          <option value={p.ich.id} data-art="ich"
            {...personOption(leute.find((u) => u.id === p.ich.id) ?? p.ich)}>{p.ich.name}</option>
          {leute.filter((u) => u.id !== p.ich.id)
            .map((u) => <option key={u.id} value={u.id} {...personOption(u)}>{u.name}</option>)}
        </Auswahlmenue>
        <Auswahl beschriftung="Spalte" wert={spalte} gesperrt={weg.laeuft}
          {...(weg.feldFehler.columnId !== undefined ? { fehler: weg.feldFehler.columnId } : {})}
          onGeaendert={(e) => { setSpalte(e.wert); setSpalteBeruehrt(true); }}>
          {spalten.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </Auswahl>
        <Feld id="ta-datum" label="Wiedervorlage" typ="date" wert={datum} bearbeitbar gesperrt={weg.laeuft}
          onChange={(w) => { setDatum(w); setDatumBeruehrt(true); weg.feldRaeumen("resubmitDate"); }}
          fehler={weg.feldFehler.resubmitDate} hilfe="Wochenende/Feiertag nur Hinweis" />
        <Feld id="ta-beschreibung" label="Beschreibung" typ="mehrzeilig" wert={beschreibung} bearbeitbar
          gesperrt={weg.laeuft} max={20000} hilfe="Markdown: **fett**, _kursiv_, Listen"
          onChange={(w) => { setBeschreibung(w); weg.feldRaeumen("descriptionMarkdown"); }}
          fehler={weg.feldFehler.descriptionMarkdown} />
        {/* Kunden wie die übrigen Felder mit der Beschriftung oben; „ohne Kunde (intern)“ ist die
            Hilfe des leeren Felds, kein Text daneben (Sichtprobe 11). */}
        <div className="ta__kunden">
          {p.kunde === undefined ? (
            <Suchfeld name="Kunde suchen" beschriftung="Kunden" platzhalter="Kunde zuordnen" wert={suche}
              gesperrt={weg.laeuft} hilfe={kunden.length === 0 ? "ohne Kunde (intern)" : undefined}
              onText={(t) => { void suchen(t); }} />
          ) : <span className="ta__leise">Kunden</span>}
          {kunden.length > 0 && (
            <span className="ta__chips">
              {kunden.map((k) => (
                <Chip key={k.id} {...hausfarbeChip(p.companies, kuerzel)}
                  {...(p.kunde === undefined
                    ? { entfernbar: true, onEntfernt: () => setKunden(kunden.filter((x) => x.id !== k.id)) } : {})}>
                  {k.name}
                </Chip>
              ))}
            </span>
          )}
          {p.kunde === undefined && treffer.length > 0 && (
            <span className="ta__chips">
              {treffer.map((k) => (
                <KiKnopf key={k.id} art="leise" onClick={() => {
                  if (!kunden.some((x) => x.id === k.id)) setKunden([...kunden, { id: k.id, name: k.companyName }]);
                  setSuche(""); setTreffer([]);
                }}>{`${k.companyName} zuordnen`}</KiKnopf>
              ))}
            </span>
          )}
        </div>
      </Formular>
      <SchreibHinweis meldung={weg.meldung} />
      <ModalFuss speichernText="Anlegen" onSpeichern={() => { void anlegen(); }} onAbbrechen={() => { void abbrechen(); }}
        speichernGesperrt={weg.laeuft || vorgabeLaedt || companyId === "" || spalte === ""} abbrechenGesperrt={weg.laeuft} />
    </Modal>
  );
}
